"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseRelayAdaptReturnValue = exports.getRelayAdaptTransactionError = exports.generateCrossContractCallsProof = exports.gasEstimateForUnprovenCrossContractCalls = exports.populateProvedCrossContractCalls = exports.createNFTTokenDataFromRailgunNFTAmount = exports.createRelayAdaptUnshieldNFTAmountRecipients = exports.createRelayAdaptUnshieldERC20AmountRecipients = void 0;
const shared_models_1 = require("@railgun-community/shared-models");
const providers_1 = require("../railgun/core/providers");
const tx_generator_1 = require("./tx-generator");
const proof_cache_1 = require("./proof-cache");
const logger_1 = require("../../utils/logger");
const engine_1 = require("@railgun-community/engine");
const blocked_address_1 = require("../../utils/blocked-address");
const tx_gas_relayer_fee_estimator_1 = require("./tx-gas-relayer-fee-estimator");
const error_1 = require("../../utils/error");
const createValidCrossContractCalls = (crossContractCalls) => {
    if (!crossContractCalls.length) {
        throw new Error('No cross contract calls in transaction.');
    }
    try {
        return crossContractCalls.map(transactionRequest => {
            if (!transactionRequest.to || !transactionRequest.data) {
                throw new Error(`Cross-contract calls require 'to' and 'data' fields.`);
            }
            const transaction = {
                to: transactionRequest.to,
                value: transactionRequest.value,
                data: (0, engine_1.hexlify)(transactionRequest.data, true),
            };
            (0, blocked_address_1.assertNotBlockedAddress)(transaction.to);
            return transaction;
        });
    }
    catch (err) {
        if (!(err instanceof Error)) {
            throw err;
        }
        throw (0, error_1.reportAndSanitizeError)(createValidCrossContractCalls.name, err);
    }
};
const createRelayAdaptUnshieldERC20AmountRecipients = (networkName, unshieldERC20Amounts) => {
    const relayAdaptContract = (0, providers_1.getRelayAdaptContractForNetwork)(networkName);
    const unshieldERC20AmountRecipients = unshieldERC20Amounts.map(unshieldERC20Amount => ({
        ...unshieldERC20Amount,
        recipientAddress: relayAdaptContract.address,
    }));
    return unshieldERC20AmountRecipients;
};
exports.createRelayAdaptUnshieldERC20AmountRecipients = createRelayAdaptUnshieldERC20AmountRecipients;
const createRelayAdaptUnshieldNFTAmountRecipients = (networkName, unshieldNFTAmounts) => {
    const relayAdaptContract = (0, providers_1.getRelayAdaptContractForNetwork)(networkName);
    const unshieldNFTAmountRecipients = unshieldNFTAmounts.map(unshieldNFTAmount => ({
        ...unshieldNFTAmount,
        recipientAddress: relayAdaptContract.address,
    }));
    return unshieldNFTAmountRecipients;
};
exports.createRelayAdaptUnshieldNFTAmountRecipients = createRelayAdaptUnshieldNFTAmountRecipients;
const createNFTTokenDataFromRailgunNFTAmount = (nftAmount) => {
    return {
        tokenAddress: (0, engine_1.formatToByteLength)(nftAmount.nftAddress, engine_1.ByteLength.Address, true),
        tokenType: nftAmount.nftTokenType,
        tokenSubID: (0, engine_1.formatToByteLength)(nftAmount.tokenSubID, engine_1.ByteLength.UINT_256, true),
    };
};
exports.createNFTTokenDataFromRailgunNFTAmount = createNFTTokenDataFromRailgunNFTAmount;
const createRelayAdaptShieldNFTRecipients = (relayAdaptShieldNFTRecipients) => {
    return relayAdaptShieldNFTRecipients.map((nftRecipient) => ({
        nftTokenData: (0, exports.createNFTTokenDataFromRailgunNFTAmount)(nftRecipient),
        recipientAddress: nftRecipient.recipientAddress,
    }));
};
const populateProvedCrossContractCalls = async (networkName, railgunWalletID, relayAdaptUnshieldERC20Amounts, relayAdaptUnshieldNFTAmounts, relayAdaptShieldERC20Recipients, relayAdaptShieldNFTRecipients, crossContractCalls, relayerFeeERC20AmountRecipient, sendWithPublicWallet, overallBatchMinGasPrice, gasDetails) => {
    try {
        const { transaction, nullifiers } = await (0, proof_cache_1.populateProvedTransaction)(networkName, shared_models_1.ProofType.CrossContractCalls, railgunWalletID, false, // showSenderAddressToRecipient
        undefined, // memoText
        [], // erc20AmountRecipients
        [], // nftAmountRecipients
        relayAdaptUnshieldERC20Amounts, relayAdaptUnshieldNFTAmounts, relayAdaptShieldERC20Recipients, relayAdaptShieldNFTRecipients, crossContractCalls, relayerFeeERC20AmountRecipient, sendWithPublicWallet, overallBatchMinGasPrice, gasDetails);
        delete transaction.from;
        return {
            nullifiers,
            transaction,
        };
    }
    catch (err) {
        throw (0, error_1.reportAndSanitizeError)(exports.populateProvedCrossContractCalls.name, err);
    }
};
exports.populateProvedCrossContractCalls = populateProvedCrossContractCalls;
const gasEstimateForUnprovenCrossContractCalls = async (networkName, railgunWalletID, encryptionKey, relayAdaptUnshieldERC20Amounts, relayAdaptUnshieldNFTAmounts, relayAdaptShieldERC20Recipients, relayAdaptShieldNFTRecipients, crossContractCalls, originalGasDetails, feeTokenDetails, sendWithPublicWallet, minGasLimit) => {
    try {
        (0, proof_cache_1.setCachedProvedTransaction)(undefined);
        const overallBatchMinGasPrice = 0n;
        const validCrossContractCalls = createValidCrossContractCalls(crossContractCalls);
        const relayAdaptContract = (0, providers_1.getRelayAdaptContractForNetwork)(networkName);
        const relayAdaptUnshieldERC20AmountRecipients = (0, exports.createRelayAdaptUnshieldERC20AmountRecipients)(networkName, relayAdaptUnshieldERC20Amounts);
        const relayAdaptUnshieldNFTAmountRecipients = (0, exports.createRelayAdaptUnshieldNFTAmountRecipients)(networkName, relayAdaptUnshieldNFTAmounts);
        const shieldRandom = (0, engine_1.randomHex)(16);
        const relayShieldRequests = await engine_1.RelayAdaptHelper.generateRelayShieldRequests(shieldRandom, relayAdaptShieldERC20Recipients, createRelayAdaptShieldNFTRecipients(relayAdaptShieldNFTRecipients));
        const minimumGasLimit = minGasLimit ?? engine_1.MINIMUM_RELAY_ADAPT_CROSS_CONTRACT_CALLS_GAS_LIMIT;
        const response = await (0, tx_gas_relayer_fee_estimator_1.gasEstimateResponseDummyProofIterativeRelayerFee)((relayerFeeERC20Amount) => (0, tx_generator_1.generateDummyProofTransactions)(shared_models_1.ProofType.CrossContractCalls, networkName, railgunWalletID, encryptionKey, false, // showSenderAddressToRecipient
        undefined, // memoText
        relayAdaptUnshieldERC20AmountRecipients, relayAdaptUnshieldNFTAmountRecipients, relayerFeeERC20Amount, sendWithPublicWallet, overallBatchMinGasPrice), async (txs) => {
            const relayAdaptParamsRandom = (0, engine_1.randomHex)(31);
            // TODO: We should add the relay adapt contract gas limit here.
            const transaction = await relayAdaptContract.populateCrossContractCalls(txs, validCrossContractCalls, relayShieldRequests, relayAdaptParamsRandom, true, // isGasEstimate
            !sendWithPublicWallet, // isRelayerTransaction
            minimumGasLimit);
            // Remove gasLimit, we'll set to the minimum below.
            // TODO: Remove after callbacks upgrade.
            delete transaction.gasLimit;
            return transaction;
        }, networkName, railgunWalletID, relayAdaptUnshieldERC20AmountRecipients, originalGasDetails, feeTokenDetails, sendWithPublicWallet, true);
        // TODO: Remove this after callbacks upgrade.
        // If gas estimate is under the cross-contract-minimum, replace it with the minimum.
        if (response.gasEstimate) {
            if (response.gasEstimate < minimumGasLimit) {
                response.gasEstimate = minimumGasLimit;
            }
        }
        return response;
    }
    catch (err) {
        throw (0, error_1.reportAndSanitizeError)(exports.gasEstimateForUnprovenCrossContractCalls.name, err);
    }
};
exports.gasEstimateForUnprovenCrossContractCalls = gasEstimateForUnprovenCrossContractCalls;
const generateCrossContractCallsProof = async (networkName, railgunWalletID, encryptionKey, relayAdaptUnshieldERC20Amounts, relayAdaptUnshieldNFTAmounts, relayAdaptShieldERC20Recipients, relayAdaptShieldNFTRecipients, crossContractCalls, relayerFeeERC20AmountRecipient, sendWithPublicWallet, overallBatchMinGasPrice, minGasLimit, progressCallback) => {
    try {
        (0, proof_cache_1.setCachedProvedTransaction)(undefined);
        const validCrossContractCalls = createValidCrossContractCalls(crossContractCalls);
        const relayAdaptContract = (0, providers_1.getRelayAdaptContractForNetwork)(networkName);
        const relayAdaptUnshieldERC20AmountRecipients = (0, exports.createRelayAdaptUnshieldERC20AmountRecipients)(networkName, relayAdaptUnshieldERC20Amounts);
        const relayAdaptUnshieldNFTAmountRecipients = (0, exports.createRelayAdaptUnshieldNFTAmountRecipients)(networkName, relayAdaptUnshieldNFTAmounts);
        // Generate dummy txs for relay adapt params.
        const dummyUnshieldTxs = await (0, tx_generator_1.generateDummyProofTransactions)(shared_models_1.ProofType.CrossContractCalls, networkName, railgunWalletID, encryptionKey, false, // showSenderAddressToRecipient
        undefined, // memoText
        relayAdaptUnshieldERC20AmountRecipients, relayAdaptUnshieldNFTAmountRecipients, relayerFeeERC20AmountRecipient, sendWithPublicWallet, overallBatchMinGasPrice);
        // Generate relay adapt params from dummy transactions.
        const shieldRandom = (0, engine_1.randomHex)(16);
        const relayShieldRequests = await engine_1.RelayAdaptHelper.generateRelayShieldRequests(shieldRandom, relayAdaptShieldERC20Recipients, createRelayAdaptShieldNFTRecipients(relayAdaptShieldNFTRecipients));
        const minimumGasLimit = minGasLimit ?? engine_1.MINIMUM_RELAY_ADAPT_CROSS_CONTRACT_CALLS_GAS_LIMIT;
        const isRelayerTransaction = !sendWithPublicWallet;
        const relayAdaptParamsRandom = (0, engine_1.randomHex)(31);
        const relayAdaptParams = await relayAdaptContract.getRelayAdaptParamsCrossContractCalls(dummyUnshieldTxs, validCrossContractCalls, relayShieldRequests, relayAdaptParamsRandom, isRelayerTransaction, minimumGasLimit);
        const relayAdaptID = {
            contract: relayAdaptContract.address,
            parameters: relayAdaptParams,
        };
        // Create real transactions with relay adapt params.
        const transactions = await (0, tx_generator_1.generateProofTransactions)(shared_models_1.ProofType.CrossContractCalls, networkName, railgunWalletID, encryptionKey, false, // showSenderAddressToRecipient
        undefined, // memoText
        relayAdaptUnshieldERC20AmountRecipients, relayAdaptUnshieldNFTAmountRecipients, relayerFeeERC20AmountRecipient, sendWithPublicWallet, relayAdaptID, false, // useDummyProof
        overallBatchMinGasPrice, progressCallback);
        const nullifiers = (0, tx_generator_1.nullifiersForTransactions)(transactions);
        const transaction = await relayAdaptContract.populateCrossContractCalls(transactions, validCrossContractCalls, relayShieldRequests, relayAdaptParamsRandom, false, // isGasEstimate
        isRelayerTransaction, minimumGasLimit);
        delete transaction.from;
        (0, proof_cache_1.setCachedProvedTransaction)({
            proofType: shared_models_1.ProofType.CrossContractCalls,
            railgunWalletID,
            showSenderAddressToRecipient: false,
            memoText: undefined,
            erc20AmountRecipients: [],
            nftAmountRecipients: [],
            relayAdaptUnshieldERC20Amounts,
            relayAdaptUnshieldNFTAmounts,
            relayAdaptShieldERC20Recipients,
            relayAdaptShieldNFTRecipients,
            crossContractCalls: validCrossContractCalls,
            relayerFeeERC20AmountRecipient,
            sendWithPublicWallet,
            transaction,
            overallBatchMinGasPrice,
            nullifiers,
        });
    }
    catch (err) {
        throw (0, error_1.reportAndSanitizeError)(exports.generateCrossContractCallsProof.name, err);
    }
};
exports.generateCrossContractCallsProof = generateCrossContractCallsProof;
const getRelayAdaptTransactionError = (receiptLogs) => {
    try {
        const relayAdaptError = engine_1.RelayAdaptContract.getRelayAdaptCallError(receiptLogs);
        if ((0, shared_models_1.isDefined)(relayAdaptError)) {
            (0, logger_1.sendErrorMessage)(relayAdaptError);
            return relayAdaptError;
        }
        return undefined;
    }
    catch (err) {
        throw (0, error_1.reportAndSanitizeError)(exports.getRelayAdaptTransactionError.name, err);
    }
};
exports.getRelayAdaptTransactionError = getRelayAdaptTransactionError;
const parseRelayAdaptReturnValue = (data) => {
    try {
        const relayAdaptErrorParsed = engine_1.RelayAdaptContract.parseRelayAdaptReturnValue(data);
        if (relayAdaptErrorParsed) {
            (0, logger_1.sendErrorMessage)(relayAdaptErrorParsed.error);
            return relayAdaptErrorParsed.error;
        }
        return undefined;
    }
    catch (err) {
        throw (0, error_1.reportAndSanitizeError)(exports.getRelayAdaptTransactionError.name, err);
    }
};
exports.parseRelayAdaptReturnValue = parseRelayAdaptReturnValue;
